import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useEquipment } from '../../../context/EquipmentContext';
import { useAuth } from '../../../context/AuthContext';
import { useTeam } from '../../../context/TeamContext';
import { useBed } from '../../../context/BedContext';
import { EQUIPMENT_TYPES, EQUIPMENT_STATUS, INSPECTION_ROUNDS } from '../../../data/nurseAppModels';
import { serverTimestamp, collection, query, where, getDocs, addDoc, orderBy, doc, updateDoc, increment, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';

const InspectionJourneyPage = () => {
  const navigate = useNavigate();
  const { id: inspectionId } = useParams();
  const location = useLocation();
  const { equipment, createInspection, submitInspection, updateInspection } = useEquipment();
  const { currentUser } = useAuth();
  const { currentTeam } = useTeam();
  const { beds, getAvailableBeds } = useBed();

  const [currentStep, setCurrentStep] = useState(1);
  const [inspectionData, setInspectionData] = useState({
    date: new Date(),
    round: null,
    shiftType: null,
    notes: ''
  });

  // V2: New state for flexible selection
  const [selectedSmallItems, setSelectedSmallItems] = useState([]); // Array of equipment IDs
  const [selectedLargeItems, setSelectedLargeItems] = useState([]); // Array of equipment IDs
  const [smallItemsResults, setSmallItemsResults] = useState({}); // Object: { equipmentId: { ... } }
  const [largeItemsResults, setLargeItemsResults] = useState({}); // Object: { equipmentId: { ... } }
  const [currentInspectingItem, setCurrentInspectingItem] = useState(null); // { type: 'small'|'large', id: string }
  
  // Common reasons for damage and loss
  const [damageReasons, setDamageReasons] = useState([]);
  const [lossReasons, setLossReasons] = useState([]);
  const [loadingReasons, setLoadingReasons] = useState(false);
  
  // Inspection state
  const [currentInspectionId, setCurrentInspectionId] = useState(inspectionId || null);
  const [inspectionStatus, setInspectionStatus] = useState('DRAFT'); // DRAFT, PENDING_APPROVAL, APPROVED, REJECTED
  const [isLoadingInspection, setIsLoadingInspection] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Load existing inspection if editing
  useEffect(() => {
    const loadInspection = async () => {
      if (!currentInspectionId || !currentTeam) return;
      
      try {
        setIsLoadingInspection(true);
        const inspectionRef = doc(db, 'equipmentInspections', currentInspectionId);
        const inspectionSnap = await getDoc(inspectionRef);
        
        if (inspectionSnap.exists()) {
          const data = inspectionSnap.data();
          
          // Check if user is the inspector
          if (data.inspectedBy !== currentUser?.uid) {
            alert('คุณไม่มีสิทธิ์แก้ไขรายการนี้');
            navigate('/nurse/equipment');
            return;
          }
          
          // Only allow edit if status is DRAFT, PENDING_APPROVAL, or REJECTED
          if (data.status === 'APPROVED') {
            alert('ไม่สามารถแก้ไขรายการที่ได้รับการอนุมัติแล้ว');
            navigate(`/nurse/equipment/inspections/${inspectionId}/review`);
            return;
          }
          
          setInspectionStatus(data.status);
          
          // Load inspection data
          if (data.date) {
            const date = data.date.toDate ? data.date.toDate() : new Date(data.date);
            setInspectionData({
              date: date,
              round: data.round,
              shiftType: data.shiftType,
              smallItems: [],
              largeItems: [],
              notes: data.notes || ''
            });
          }
          
          // Load items results
          if (data.items && Array.isArray(data.items)) {
            const smallItemsData = {};
            const largeItemsData = {};
            const selectedSmall = [];
            const selectedLarge = [];
            
            data.items.forEach(item => {
              if (item.type === EQUIPMENT_TYPES.SMALL_ITEM) {
                smallItemsData[item.equipmentId] = item;
                if (!selectedSmall.includes(item.equipmentId)) {
                  selectedSmall.push(item.equipmentId);
                }
              } else {
                largeItemsData[item.equipmentId] = item;
                if (!selectedLarge.includes(item.equipmentId)) {
                  selectedLarge.push(item.equipmentId);
                }
              }
            });
            
            setSmallItemsResults(smallItemsData);
            setLargeItemsResults(largeItemsData);
            setSelectedSmallItems(selectedSmall);
            setSelectedLargeItems(selectedLarge);
            
            // Set first item as current if exists
            if (selectedSmall.length > 0) {
              setCurrentInspectingItem({ type: 'small', id: selectedSmall[0] });
            } else if (selectedLarge.length > 0) {
              setCurrentInspectingItem({ type: 'large', id: selectedLarge[0] });
            }
          }
        }
      } catch (err) {
        console.error('Error loading inspection:', err);
        alert(`เกิดข้อผิดพลาดในการโหลดข้อมูล: ${err.message}`);
      } finally {
        setIsLoadingInspection(false);
      }
    };
    
    loadInspection();
  }, [currentInspectionId, currentTeam, currentUser, navigate]);

  // Filter equipment by type
  const smallItems = equipment.filter(eq => eq.type === EQUIPMENT_TYPES.SMALL_ITEM);
  // Filter out borrowed equipment from inspection list
  const largeItems = equipment.filter(eq => 
    eq.type === EQUIPMENT_TYPES.LARGE_ITEM && 
    eq.status !== EQUIPMENT_STATUS.BORROWED
  );

  // Inspection rounds configuration
  const inspectionRounds = [
    { value: INSPECTION_ROUNDS.MORNING_BEFORE, label: 'เช้า - ก่อนเข้าเวร', shiftType: 'MORNING' },
    { value: INSPECTION_ROUNDS.MORNING_AFTER, label: 'เช้า - หลังเข้าเวร', shiftType: 'MORNING' },
    { value: INSPECTION_ROUNDS.AFTERNOON_BEFORE, label: 'บ่าย - ก่อนเข้าเวร', shiftType: 'AFTERNOON' },
    { value: INSPECTION_ROUNDS.AFTERNOON_AFTER, label: 'บ่าย - หลังเข้าเวร', shiftType: 'AFTERNOON' },
    { value: INSPECTION_ROUNDS.NIGHT_BEFORE, label: 'ดึก - ก่อนเข้าเวร', shiftType: 'NIGHT' },
    { value: INSPECTION_ROUNDS.NIGHT_AFTER, label: 'ดึก - หลังเข้าเวร', shiftType: 'NIGHT' }
  ];

  // Calculate progress (V2: Updated for 6 steps)
  const calculateProgress = () => {
    const stepWeight = 100 / 6; // ~16.67% per step
    const completedSteps = currentStep - 1;
    
    let currentStepProgress = 0;
    if (currentStep === 2) {
      // Step 2: Selection progress (100% if at least one selected)
      currentStepProgress = selectedSmallItems.length > 0 ? 100 : 0;
    } else if (currentStep === 3) {
      // Step 3: Small items inspection progress
      const completed = Object.keys(smallItemsResults).length;
      currentStepProgress = selectedSmallItems.length > 0 ? (completed / selectedSmallItems.length) * 100 : 100;
    } else if (currentStep === 4) {
      // Step 4: Selection progress (100% if at least one selected)
      currentStepProgress = selectedLargeItems.length > 0 ? 100 : 0;
    } else if (currentStep === 5) {
      // Step 5: Large items inspection progress
      const completed = Object.keys(largeItemsResults).length;
      currentStepProgress = selectedLargeItems.length > 0 ? (completed / selectedLargeItems.length) * 100 : 100;
    } else if (currentStep === 6) {
      currentStepProgress = 100;
    }
    
    return (completedSteps * stepWeight) + (currentStepProgress * stepWeight / 100);
  };

  // Step 1: Select Round
  const Step1SelectRound = () => {
    return (
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            📅 วันที่
          </label>
          <input
            type="date"
            value={inspectionData.date.toISOString().split('T')[0]}
            onChange={(e) => setInspectionData({
              ...inspectionData,
              date: new Date(e.target.value)
            })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            🔄 เลือกรอบตรวจสอบ
          </label>
          <div className="space-y-2">
            {inspectionRounds.map(round => (
              <label
                key={round.value}
                className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition ${
                  inspectionData.round === round.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="round"
                  value={round.value}
                  checked={inspectionData.round === round.value}
                  onChange={(e) => setInspectionData({
                    ...inspectionData,
                    round: e.target.value,
                    shiftType: round.shiftType
                  })}
                  className="mr-3"
                />
                <span className="text-gray-800">{round.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-gray-700">
            <strong>ℹ️ วัตถุประสงค์:</strong><br />
            ตรวจสอบจำนวนคงเหลือ, เสียหาย, สูญหาย เพื่อส่งต่อเวรถัดไป
          </p>
        </div>
      </div>
    );
  };

  // Step 2: Select Small Items (V2 - New)
  const Step2SelectSmallItems = () => {
    const handleToggleItem = (itemId) => {
      setSelectedSmallItems(prev => 
        prev.includes(itemId) 
          ? prev.filter(id => id !== itemId)
          : [...prev, itemId]
      );
    };

    const handleSelectAll = () => {
      setSelectedSmallItems(smallItems.map(eq => eq.id));
    };

    const handleDeselectAll = () => {
      setSelectedSmallItems([]);
    };

    const handleSkipToLargeItems = () => {
      if (smallItems.length > 0 && selectedSmallItems.length === 0) {
        const confirmSkip = window.confirm(
          'คุณยังไม่ได้เลือกอุปกรณ์ชิ้นเล็ก\n\n' +
          'หากไม่มีอุปกรณ์ชิ้นเล็กที่ต้องการตรวจสอบ คุณสามารถข้ามไปเลือกอุปกรณ์ชิ้นใหญ่ได้\n\n' +
          'ต้องการข้ามไปเลือกอุปกรณ์ชิ้นใหญ่หรือไม่?'
        );
        if (!confirmSkip) {
          return;
        }
      }
      setCurrentStep(4);
    };

    if (smallItems.length === 0) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-600 mb-4">ไม่มีอุปกรณ์ชิ้นเล็ก</p>
          <button
            onClick={handleSkipToLargeItems}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            ข้ามไปเลือกอุปกรณ์ชิ้นใหญ่
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-gray-700">
            <strong>💡 เคล็ดลับ:</strong><br />
            เลือกอุปกรณ์ที่ต้องการตรวจสอบ สามารถตรวจสอบได้ไม่เรียงลำดับ
          </p>
        </div>

        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">
            📦 เลือกอุปกรณ์ชิ้นเล็กที่จะตรวจสอบ
          </h3>
          <div className="flex gap-2">
            <button
              onClick={handleSelectAll}
              className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
            >
              เลือกทั้งหมด
            </button>
            <button
              onClick={handleDeselectAll}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            >
              ยกเลิกทั้งหมด
            </button>
          </div>
        </div>

        <div className="space-y-2">
          {smallItems.map(item => {
            const isSelected = selectedSmallItems.includes(item.id);
            return (
              <label
                key={item.id}
                className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleToggleItem(item.id)}
                  className="mr-3 w-5 h-5 text-blue-600 flex-shrink-0"
                />
                {/* Equipment Image */}
                {(() => {
                  const equipmentImage = (item.images && Array.isArray(item.images) && item.images.length > 0)
                    ? (typeof item.images[0] === 'string' ? item.images[0] : item.images[0]?.url)
                    : (item.thumbnail || null);
                  
                  return equipmentImage ? (
                    <div className="w-12 h-12 flex-shrink-0 mr-3 rounded-lg overflow-hidden bg-gray-100">
                      <img
                        src={equipmentImage}
                        alt={item.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.classList.add('bg-gray-200');
                          e.target.parentElement.innerHTML = '<span class="text-gray-400 text-lg flex items-center justify-center h-full">🩺</span>';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-12 h-12 flex-shrink-0 mr-3 rounded-lg bg-gray-200 flex items-center justify-center">
                      <span className="text-gray-400 text-lg">🩺</span>
                    </div>
                  );
                })()}
                <div className="flex-1">
                  <div className="font-medium text-gray-800">{item.name}</div>
                  <div className="text-sm text-gray-600">
                    {item.code} • Stock: {item.currentStock || 0} {item.unit}
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm font-medium text-gray-700">
            📊 เลือกแล้ว: {selectedSmallItems.length}/{smallItems.length} รายการ
          </p>
        </div>

        {/* Skip Option */}
        {selectedSmallItems.length === 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-gray-700 mb-3">
              <strong>⚠️ ยังไม่ได้เลือกอุปกรณ์ชิ้นเล็ก</strong>
            </p>
            <p className="text-sm text-gray-600 mb-3">
              หากไม่มีอุปกรณ์ชิ้นเล็กที่ต้องการตรวจสอบ คุณสามารถข้ามไปเลือกอุปกรณ์ชิ้นใหญ่ได้
            </p>
            <button
              onClick={handleSkipToLargeItems}
              className="w-full px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 font-semibold transition-colors"
            >
              ⏭️ ข้ามไปเลือกอุปกรณ์ชิ้นใหญ่
            </button>
          </div>
        )}
      </div>
    );
  };

  // Step 3: Inspect Small Items (V2 - Flexible Order)
  const Step3InspectSmallItems = () => {
    const currentItem = currentInspectingItem?.type === 'small' 
      ? smallItems.find(eq => eq.id === currentInspectingItem.id)
      : null;
    
    // Use refs to track focused input
    const availableInputRef = useRef(null);
    const damagedInputRef = useRef(null);
    const lostInputRef = useRef(null);
    const notesInputRef = useRef(null);
    
    // Track which input is currently focused
    const [focusedInput, setFocusedInput] = useState(null);
    
    // Track if user manually edited availableQuantity
    const [isAvailableQuantityManuallyEdited, setIsAvailableQuantityManuallyEdited] = useState(false);
    
    // Use local state for input values to prevent re-render issues
    const [localValues, setLocalValues] = useState({
      availableQuantity: '',
      damagedQuantity: '',
      lostQuantity: '',
      damagedReason: '',
      damagedReasonOther: '',
      damagedDescription: '',
      lostReason: '',
      lostReasonOther: '',
      lostDescription: '',
      notes: ''
    });
    
    // State for adding new reasons
    const [showAddDamagedReason, setShowAddDamagedReason] = useState(false);
    const [showAddLostReason, setShowAddLostReason] = useState(false);
    const [newDamagedReason, setNewDamagedReason] = useState('');
    const [newLostReason, setNewLostReason] = useState('');
    
    // State for success feedback
    const [saveSuccess, setSaveSuccess] = useState(false);

    // Sync local state with global state when item changes ONLY
    useEffect(() => {
      if (currentItem) {
        const savedProgress = smallItemsResults[currentItem.id];
        const previousStock = currentItem.currentStock || 0;
        const damagedQty = parseInt(savedProgress?.damagedQuantity || '0', 10);
        const lostQty = parseInt(savedProgress?.lostQuantity || '0', 10);
        // Auto-calculate available quantity: previousStock - damaged - lost
        const calculatedAvailable = Math.max(0, previousStock - damagedQty - lostQty);
        
        setLocalValues({
          availableQuantity: savedProgress?.availableQuantity ?? (calculatedAvailable > 0 ? String(calculatedAvailable) : ''),
          damagedQuantity: savedProgress?.damagedQuantity ?? '',
          lostQuantity: savedProgress?.lostQuantity ?? '',
          damagedReason: savedProgress?.damagedReason ?? '',
          damagedReasonOther: savedProgress?.damagedReasonOther ?? '',
          damagedDescription: savedProgress?.damagedDescription ?? '',
          lostReason: savedProgress?.lostReason ?? '',
          lostReasonOther: savedProgress?.lostReasonOther ?? '',
          lostDescription: savedProgress?.lostDescription ?? '',
          notes: savedProgress?.notes ?? ''
        });
        // Reset focus and manual edit flag when item changes
        setFocusedInput(null);
        setIsAvailableQuantityManuallyEdited(false);
      }
    }, [currentItem?.id]); // Sync only when item changes (removed currentStock to prevent re-render loop)

    // Restore focus after re-render (only when input is focused)
    // Removed to prevent cursor position issues

    // Memoize itemResult to prevent unnecessary re-renders
    const itemResult = useMemo(() => {
      return currentItem ? (smallItemsResults[currentItem.id] || {}) : {};
    }, [currentItem?.id, smallItemsResults]);

    // Handle adding new damaged reason
    const handleAddDamagedReason = async () => {
      if (!newDamagedReason.trim() || !currentTeam) return;
      
      try {
        // Check if reason already exists
        const existingReason = damageReasons.find(r => r.reason.toLowerCase() === newDamagedReason.trim().toLowerCase());
        if (existingReason) {
          alert('สาเหตุนี้มีอยู่แล้ว');
          return;
        }
        
        // Add to Firestore
        const reasonRef = await addDoc(collection(db, 'equipmentDamageReasons'), {
          teamId: currentTeam.id,
          reason: newDamagedReason.trim(),
          usageCount: 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        // Update local state
        setDamageReasons(prev => [...prev, {
          id: reasonRef.id,
          reason: newDamagedReason.trim(),
          usageCount: 0
        }]);
        
        // Set as selected
        setLocalValues(prev => ({ ...prev, damagedReason: newDamagedReason.trim() }));
        setSmallItemsResults(prev => ({
          ...prev,
          [currentItem.id]: {
            ...(prev[currentItem.id] || {}),
            damagedReason: newDamagedReason.trim()
          }
        }));
        
        // Reset form
        setShowAddDamagedReason(false);
        setNewDamagedReason('');
      } catch (err) {
        console.error('Error adding damaged reason:', err);
        alert(`เกิดข้อผิดพลาด: ${err.message}`);
      }
    };

    // Handle adding new lost reason
    const handleAddLostReason = async () => {
      if (!newLostReason.trim() || !currentTeam) return;
      
      try {
        // Check if reason already exists
        const existingReason = lossReasons.find(r => r.reason.toLowerCase() === newLostReason.trim().toLowerCase());
        if (existingReason) {
          alert('สาเหตุนี้มีอยู่แล้ว');
          return;
        }
        
        // Add to Firestore
        const reasonRef = await addDoc(collection(db, 'equipmentLossReasons'), {
          teamId: currentTeam.id,
          reason: newLostReason.trim(),
          usageCount: 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        // Update local state
        setLossReasons(prev => [...prev, {
          id: reasonRef.id,
          reason: newLostReason.trim(),
          usageCount: 0
        }]);
        
        // Set as selected
        setLocalValues(prev => ({ ...prev, lostReason: newLostReason.trim() }));
        setSmallItemsResults(prev => ({
          ...prev,
          [currentItem.id]: {
            ...(prev[currentItem.id] || {}),
            lostReason: newLostReason.trim()
          }
        }));
        
        // Reset form
        setShowAddLostReason(false);
        setNewLostReason('');
      } catch (err) {
        console.error('Error adding lost reason:', err);
        alert(`เกิดข้อผิดพลาด: ${err.message}`);
      }
    };

    const handleSaveItem = () => {
      if (!currentItem) {
        console.error('handleSaveItem: currentItem is null');
        return;
      }

      console.log('handleSaveItem called', { currentItem: currentItem.id, localValues });

      // Validation: Check if reason is required
      const damagedQty = parseInt(localValues.damagedQuantity || '0', 10);
      const lostQty = parseInt(localValues.lostQuantity || '0', 10);
      
      console.log('Validation check:', { damagedQty, lostQty, damagedReason: localValues.damagedReason, lostReason: localValues.lostReason });
      
      if (damagedQty > 0 && !localValues.damagedReason) {
        alert('กรุณาเลือกสาเหตุของอุปกรณ์ที่เสียหาย');
        return;
      }
      
      if (lostQty > 0 && !localValues.lostReason) {
        alert('กรุณาเลือกสาเหตุของอุปกรณ์ที่สูญหาย');
        return;
      }

      // Check if OTHER reason is selected but no other reason text provided
      if (damagedQty > 0 && localValues.damagedReason === 'OTHER' && !localValues.damagedReasonOther) {
        alert('กรุณากรอกสาเหตุอื่นๆ ของอุปกรณ์ที่เสียหาย');
        return;
      }
      
      if (lostQty > 0 && localValues.lostReason === 'OTHER' && !localValues.lostReasonOther) {
        alert('กรุณากรอกสาเหตุอื่นๆ ของอุปกรณ์ที่สูญหาย');
        return;
      }

      console.log('Validation passed, saving item data...');

      // Save current local values to global state first
      const itemData = {
        equipmentId: currentItem.id,
        equipmentName: currentItem.name,
        equipmentCode: currentItem.code,
        type: EQUIPMENT_TYPES.SMALL_ITEM,
        previousStock: currentItem.currentStock || 0,
        availableQuantity: parseInt(localValues.availableQuantity || '0', 10) || 0,
        damagedQuantity: parseInt(localValues.damagedQuantity || '0', 10) || 0,
        lostQuantity: parseInt(localValues.lostQuantity || '0', 10) || 0,
        damagedReason: localValues.damagedReason || '',
        damagedReasonOther: localValues.damagedReasonOther || '',
        damagedDescription: localValues.damagedDescription || '',
        lostReason: localValues.lostReason || '',
        lostReasonOther: localValues.lostReasonOther || '',
        lostDescription: localValues.lostDescription || '',
        notes: localValues.notes || ''
      };

      console.log('Saving item data:', itemData);

      setSmallItemsResults(prev => {
        const updated = {
          ...prev,
          [currentItem.id]: itemData
        };
        console.log('Updated smallItemsResults:', updated);
        return updated;
      });

      console.log('Item saved successfully');

      // Show visual feedback
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);

      // Show success feedback
      const currentIndex = selectedSmallItems.findIndex(id => id === currentItem.id);
      const remainingItems = selectedSmallItems.length - (currentIndex + 1);
      
      if (remainingItems > 0) {
        // Show success message and move to next item
        setTimeout(() => {
          alert(`✅ บันทึกสำเร็จ!\n\nบันทึกข้อมูล "${currentItem.name}" แล้ว\n\nเหลืออีก ${remainingItems} รายการ`);
          setCurrentInspectingItem({ type: 'small', id: selectedSmallItems[currentIndex + 1] });
        }, 100);
      } else {
        // All items completed - check if there are large items to inspect
        const hasLargeItems = largeItems && largeItems.length > 0;
        
        setTimeout(() => {
          if (hasLargeItems) {
            alert(`✅ บันทึกสำเร็จ!\n\nบันทึกข้อมูล "${currentItem.name}" แล้ว\n\nตรวจสอบครบทุกรายการชิ้นเล็กแล้ว\n\nจะไปเลือกอุปกรณ์ชิ้นใหญ่ต่อ`);
            // Navigate to Step 4 (Select Large Items)
            setCurrentStep(4);
          } else {
            alert(`✅ บันทึกสำเร็จ!\n\nบันทึกข้อมูล "${currentItem.name}" แล้ว\n\nตรวจสอบครบทุกรายการแล้ว\n\nจะไปหน้าสรุปและส่ง`);
            // Navigate to Step 6 (Review and Submit)
            setCurrentStep(6);
          }
          console.log('All items completed, navigating to next step');
        }, 100);
      }
    };

    // Check if save button should be disabled - memoized to prevent unnecessary re-renders
    // Must be called before any early returns to follow React Hooks rules
    const isSaveDisabled = useMemo(() => {
      const damagedQty = parseInt(localValues.damagedQuantity || '0', 10);
      const lostQty = parseInt(localValues.lostQuantity || '0', 10);
      return (
        (damagedQty > 0 && !localValues.damagedReason) ||
        (lostQty > 0 && !localValues.lostReason) ||
        (damagedQty > 0 && localValues.damagedReason === 'OTHER' && !localValues.damagedReasonOther) ||
        (lostQty > 0 && localValues.lostReason === 'OTHER' && !localValues.lostReasonOther)
      );
    }, [
      localValues.damagedQuantity,
      localValues.lostQuantity,
      localValues.damagedReason,
      localValues.lostReason,
      localValues.damagedReasonOther,
      localValues.lostReasonOther
    ]);

    if (selectedSmallItems.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
            <p className="text-lg font-semibold text-gray-800 mb-2">
              ⚠️ ยังไม่ได้เลือกอุปกรณ์ชิ้นเล็ก
            </p>
            <p className="text-sm text-gray-600 mb-4">
              กรุณาเลือกอุปกรณ์ชิ้นเล็กที่ต้องการตรวจสอบ
            </p>
            <button
              onClick={() => setCurrentStep(2)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
            >
              ← กลับไปเลือกอุปกรณ์ชิ้นเล็ก
            </button>
          </div>
        </div>
      );
    }

    const completedCount = Object.keys(smallItemsResults).length;
    const selectedItems = selectedSmallItems.map(id => smallItems.find(eq => eq.id === id)).filter(Boolean);

    return (
      <div className="space-y-6">
        {/* Selected Items List (Top) */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            📋 รายการที่เลือก ({selectedSmallItems.length} รายการ)
          </h3>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {selectedItems.map(item => {
              const isDone = smallItemsResults[item.id];
              const isCurrent = currentInspectingItem?.id === item.id;
              return (
                <div
                  key={item.id}
                  onClick={() => setCurrentInspectingItem({ type: 'small', id: item.id })}
                  className={`flex items-center p-2 rounded cursor-pointer transition ${
                    isCurrent ? 'bg-blue-100 border-2 border-blue-500' : 
                    isDone ? 'bg-green-50 border border-green-200' : 
                    'bg-gray-50 border border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <span className="mr-2">
                    {isDone ? '✅' : isCurrent ? '🔄' : '⏳'}
                  </span>
                  {/* Equipment Image */}
                  {(() => {
                    const equipmentImage = (item.images && Array.isArray(item.images) && item.images.length > 0)
                      ? (typeof item.images[0] === 'string' ? item.images[0] : item.images[0]?.url)
                      : (item.thumbnail || null);
                    
                    return equipmentImage ? (
                      <div className="w-8 h-8 flex-shrink-0 mr-2 rounded overflow-hidden bg-gray-100">
                        <img
                          src={equipmentImage}
                          alt={item.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.classList.add('bg-gray-200');
                            e.target.parentElement.innerHTML = '<span class="text-gray-400 text-xs flex items-center justify-center h-full">🩺</span>';
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-8 h-8 flex-shrink-0 mr-2 rounded bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-400 text-xs">🩺</span>
                      </div>
                    );
                  })()}
                  <span className="text-sm text-gray-800 flex-1">{item.name}</span>
                  {isCurrent && <span className="ml-auto text-xs text-blue-600">(กำลังตรวจ)</span>}
                  {isDone && !isCurrent && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentInspectingItem({ type: 'small', id: item.id });
                      }}
                      className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      ดูผล
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Current Item Form */}
        {currentItem && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              📦 {currentItem.name} ({currentItem.code})
            </h3>

            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-2">Stock ก่อนหน้า:</p>
                <p className="text-xl font-bold text-gray-800">
                  {currentItem.currentStock || 0} {currentItem.unit}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📊 นับจำนวนจริง:
                </label>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">เสียหาย</label>
                    <input
                      ref={damagedInputRef}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={localValues.damagedQuantity}
                      onFocus={() => {
                        setFocusedInput('damaged');
                      }}
                      onBlur={() => {
                        setFocusedInput(null);
                        // Auto-calculate available quantity when damaged changes
                        const damagedQty = parseInt(localValues.damagedQuantity || '0', 10);
                        const lostQty = parseInt(localValues.lostQuantity || '0', 10);
                        const previousStock = currentItem.currentStock || 0;
                        const calculatedAvailable = Math.max(0, previousStock - damagedQty - lostQty);
                        if (!localValues.availableQuantity || localValues.availableQuantity === '') {
                          setLocalValues(prev => ({ ...prev, availableQuantity: calculatedAvailable > 0 ? String(calculatedAvailable) : '' }));
                        }
                      }}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        
                        // Auto-calculate available quantity
                        const damagedQty = parseInt(value || '0', 10);
                        const lostQty = parseInt(localValues.lostQuantity || '0', 10);
                        const previousStock = currentItem.currentStock || 0;
                        const calculatedAvailable = Math.max(0, previousStock - damagedQty - lostQty);
                        
                        // Update local state - auto-calculate availableQuantity unless user manually edited it
                        setLocalValues(prev => ({
                          ...prev,
                          damagedQuantity: value,
                          availableQuantity: isAvailableQuantityManuallyEdited 
                            ? prev.availableQuantity 
                            : (calculatedAvailable > 0 ? String(calculatedAvailable) : '')
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="0"
                    />
                    <span className="text-xs text-gray-500 ml-2">{currentItem.unit}</span>
                    
                    {/* Damage Reason Dropdown - Only show if quantity > 0 */}
                    {parseInt(localValues.damagedQuantity || '0', 10) > 0 && (
                      <div className="mt-2 space-y-2">
                        <div className="flex gap-2">
                          <select
                            value={localValues.damagedReason}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Update local state only - don't update global state until save
                              setLocalValues(prev => ({ ...prev, damagedReason: value, damagedReasonOther: value === 'OTHER' ? prev.damagedReasonOther : '' }));
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          >
                            <option value="">เลือกสาเหตุ</option>
                            {damageReasons.map(reason => (
                              <option key={reason.id} value={reason.reason}>
                                {reason.reason}
                              </option>
                            ))}
                            <option value="OTHER">อื่นๆ (เพิ่มใหม่)</option>
                          </select>
                          {localValues.damagedReason === 'OTHER' && (
                            <button
                              type="button"
                              onClick={() => setShowAddDamagedReason(true)}
                              className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm whitespace-nowrap"
                            >
                              + เพิ่ม
                            </button>
                          )}
                        </div>
                        
                        {/* Add new reason modal */}
                        {showAddDamagedReason && (
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={newDamagedReason}
                              onChange={(e) => setNewDamagedReason(e.target.value)}
                              placeholder="กรอกสาเหตุใหม่"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleAddDamagedReason();
                                }
                              }}
                            />
                            <button
                              type="button"
                              onClick={handleAddDamagedReason}
                              className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                            >
                              บันทึก
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowAddDamagedReason(false);
                                setNewDamagedReason('');
                              }}
                              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
                            >
                              ยกเลิก
                            </button>
                          </div>
                        )}
                        
                        {/* Other reason input */}
                        {localValues.damagedReason === 'OTHER' && (
                          <input
                            type="text"
                            value={localValues.damagedReasonOther || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Update local state only - don't update global state until save
                              setLocalValues(prev => ({ ...prev, damagedReasonOther: value }));
                            }}
                            placeholder="กรอกสาเหตุอื่นๆ"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                        )}
                        
                        {/* Description */}
                        <textarea
                          value={localValues.damagedDescription}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Update local state only - don't update global state until save
                            setLocalValues(prev => ({ ...prev, damagedDescription: value }));
                          }}
                          onFocus={() => setFocusedInput('damagedDescription')}
                          onBlur={() => setFocusedInput(null)}
                          placeholder="คำอธิบายเพิ่มเติม (ถ้ามี)"
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm relative z-10"
                          style={{ pointerEvents: 'auto' }}
                        />
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">สูญหาย</label>
                    <input
                      ref={lostInputRef}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={localValues.lostQuantity}
                      onFocus={() => {
                        setFocusedInput('lost');
                      }}
                      onBlur={() => {
                        setFocusedInput(null);
                        // Auto-calculate available quantity when lost changes
                        const damagedQty = parseInt(localValues.damagedQuantity || '0', 10);
                        const lostQty = parseInt(localValues.lostQuantity || '0', 10);
                        const previousStock = currentItem.currentStock || 0;
                        const calculatedAvailable = Math.max(0, previousStock - damagedQty - lostQty);
                        if (!localValues.availableQuantity || localValues.availableQuantity === '') {
                          setLocalValues(prev => ({ ...prev, availableQuantity: calculatedAvailable > 0 ? String(calculatedAvailable) : '' }));
                        }
                      }}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        
                        // Auto-calculate available quantity
                        const damagedQty = parseInt(localValues.damagedQuantity || '0', 10);
                        const lostQty = parseInt(value || '0', 10);
                        const previousStock = currentItem.currentStock || 0;
                        const calculatedAvailable = Math.max(0, previousStock - damagedQty - lostQty);
                        
                        // Update local state - auto-calculate availableQuantity unless user manually edited it
                        setLocalValues(prev => ({
                          ...prev,
                          lostQuantity: value,
                          availableQuantity: isAvailableQuantityManuallyEdited 
                            ? prev.availableQuantity 
                            : (calculatedAvailable > 0 ? String(calculatedAvailable) : '')
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="0"
                    />
                    <span className="text-xs text-gray-500 ml-2">{currentItem.unit}</span>
                    
                    {/* Loss Reason Dropdown - Only show if quantity > 0 */}
                    {parseInt(localValues.lostQuantity || '0', 10) > 0 && (
                      <div className="mt-2 space-y-2">
                        <div className="flex gap-2">
                          <select
                            value={localValues.lostReason}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Update local state only - don't update global state until save
                              setLocalValues(prev => ({ ...prev, lostReason: value, lostReasonOther: value === 'OTHER' ? prev.lostReasonOther : '' }));
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          >
                            <option value="">เลือกสาเหตุ</option>
                            {lossReasons.map(reason => (
                              <option key={reason.id} value={reason.reason}>
                                {reason.reason}
                              </option>
                            ))}
                            <option value="OTHER">อื่นๆ (เพิ่มใหม่)</option>
                          </select>
                          {localValues.lostReason === 'OTHER' && (
                            <button
                              type="button"
                              onClick={() => setShowAddLostReason(true)}
                              className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm whitespace-nowrap"
                            >
                              + เพิ่ม
                            </button>
                          )}
                        </div>
                        
                        {/* Add new reason modal */}
                        {showAddLostReason && (
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={newLostReason}
                              onChange={(e) => setNewLostReason(e.target.value)}
                              placeholder="กรอกสาเหตุใหม่"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleAddLostReason();
                                }
                              }}
                            />
                            <button
                              type="button"
                              onClick={handleAddLostReason}
                              className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                            >
                              บันทึก
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowAddLostReason(false);
                                setNewLostReason('');
                              }}
                              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
                            >
                              ยกเลิก
                            </button>
                          </div>
                        )}
                        
                        {/* Other reason input */}
                        {localValues.lostReason === 'OTHER' && (
                          <input
                            type="text"
                            value={localValues.lostReasonOther || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Update local state only - don't update global state until save
                              setLocalValues(prev => ({ ...prev, lostReasonOther: value }));
                            }}
                            placeholder="กรอกสาเหตุอื่นๆ"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                        )}
                        
                        {/* Description */}
                        <textarea
                          value={localValues.lostDescription}
                          onChange={(e) => {
                            const value = e.target.value;
                            setLocalValues(prev => ({ ...prev, lostDescription: value }));
                            setSmallItemsResults(prev => ({
                              ...prev,
                              [currentItem.id]: {
                                ...(prev[currentItem.id] || {}),
                                lostDescription: value
                              }
                            }));
                          }}
                          onFocus={() => setFocusedInput('lostDescription')}
                          onBlur={() => setFocusedInput(null)}
                          placeholder="คำอธิบายเพิ่มเติม (ถ้ามี)"
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm relative z-10"
                          style={{ pointerEvents: 'auto' }}
                        />
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">คงเหลือ</label>
                    <input
                      ref={availableInputRef}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={localValues.availableQuantity}
                      onFocus={() => {
                        setFocusedInput('available');
                        setIsAvailableQuantityManuallyEdited(true);
                      }}
                      onBlur={() => setFocusedInput(null)}
                      onChange={(e) => {
                        // Only allow numbers
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        
                        // Update local state only - don't update global state until save
                        setLocalValues(prev => ({ ...prev, availableQuantity: value }));
                        setIsAvailableQuantityManuallyEdited(true);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="0"
                    />
                    <span className="text-xs text-gray-500 ml-2">{currentItem.unit}</span>
                  </div>
                </div>
              </div>

              {/* Variance Calculation */}
              {localValues.availableQuantity && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-gray-700">
                    <strong>⚠️ แตกต่าง:</strong>{' '}
                    {parseInt(localValues.availableQuantity || '0', 10) + 
                     parseInt(localValues.damagedQuantity || '0', 10) + 
                     parseInt(localValues.lostQuantity || '0', 10) - 
                     (currentItem.currentStock || 0)}{' '}
                    {currentItem.unit}
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  หมายเหตุ
                </label>
                <textarea
                  ref={notesInputRef}
                  value={localValues.notes}
                  onFocus={() => setFocusedInput('notes')}
                  onBlur={() => setFocusedInput(null)}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Update local state only - don't update global state until save
                    setLocalValues(prev => ({ ...prev, notes: value }));
                  }}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="เพิ่มหมายเหตุ..."
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3 relative" style={{ zIndex: 60 }}>
              {/* Success Message */}
              {saveSuccess && (
                <div className="absolute -top-12 left-0 right-0 flex justify-center animate-bounce">
                  <div className="bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
                    <span className="text-xl">✅</span>
                    <span className="font-semibold">บันทึกสำเร็จ!</span>
                  </div>
                </div>
              )}
              
              <button
                type="button"
                onClick={(e) => {
                  console.log('Save button clicked - START', { 
                    isSaveDisabled, 
                    currentItem: currentItem?.id, 
                    disabled: e.currentTarget.disabled,
                    localValues,
                    damagedQty: parseInt(localValues.damagedQuantity || '0', 10),
                    lostQty: parseInt(localValues.lostQuantity || '0', 10)
                  });
                  
                  e.preventDefault();
                  e.stopPropagation();
                  
                  if (isSaveDisabled) {
                    console.warn('Save button is disabled, cannot save');
                    alert('กรุณากรอกข้อมูลให้ครบถ้วนก่อนบันทึก');
                    return;
                  }
                  
                  console.log('Save button clicked - calling handleSaveItem');
                  handleSaveItem();
                  console.log('Save button clicked - handleSaveItem called');
                }}
                disabled={isSaveDisabled}
                className={`flex-1 px-4 py-2 text-white rounded-lg transition-all ${
                  saveSuccess
                    ? 'bg-green-500 animate-pulse'
                    : isSaveDisabled 
                      ? 'bg-gray-400 opacity-50 cursor-not-allowed' 
                      : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800 cursor-pointer'
                }`}
                style={{ 
                  position: 'relative',
                  zIndex: 100,
                  pointerEvents: 'auto',
                  touchAction: 'manipulation'
                }}
              >
                {saveSuccess ? '✅ บันทึกแล้ว' : '💾 บันทึก'}
              </button>
              {completedCount < selectedSmallItems.length && (
                <button
                  type="button"
                  onClick={() => {
                    if (!isSaveDisabled) {
                      handleSaveItem();
                      const currentIndex = selectedSmallItems.findIndex(id => id === currentItem.id);
                      if (currentIndex < selectedSmallItems.length - 1) {
                        setCurrentInspectingItem({ type: 'small', id: selectedSmallItems[currentIndex + 1] });
                      }
                    } else {
                      console.warn('Save button is disabled, cannot save');
                      alert('กรุณากรอกข้อมูลให้ครบถ้วนก่อนบันทึก');
                    }
                  }}
                  disabled={isSaveDisabled}
                  className={`flex-1 px-4 py-2 bg-green-600 text-white rounded-lg transition-all ${
                    isSaveDisabled 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:bg-green-700 active:bg-green-800 cursor-pointer'
                  }`}
                  style={{ 
                    position: 'relative',
                    zIndex: 60
                  }}
                >
                  บันทึกและถัดไป →
                </button>
              )}
            </div>
          </div>
        )}

        <div className="text-center text-sm text-gray-600">
          📊 ความคืบหน้า: {completedCount}/{selectedSmallItems.length} รายการ
        </div>
      </div>
    );
  };

  // Step 4: Select Large Items (V2 - New)
  const Step4SelectLargeItems = () => {
    const handleToggleItem = (itemId) => {
      setSelectedLargeItems(prev => 
        prev.includes(itemId) 
          ? prev.filter(id => id !== itemId)
          : [...prev, itemId]
      );
    };

    const handleSelectAll = () => {
      setSelectedLargeItems(largeItems.map(eq => eq.id));
    };

    const handleDeselectAll = () => {
      setSelectedLargeItems([]);
    };

    const handleSkipToReview = () => {
      if (largeItems.length > 0 && selectedLargeItems.length === 0) {
        const confirmSkip = window.confirm(
          'คุณยังไม่ได้เลือกอุปกรณ์ชิ้นใหญ่\n\n' +
          'หากไม่มีอุปกรณ์ชิ้นใหญ่ที่ต้องการตรวจสอบ คุณสามารถข้ามไปหน้าสรุปและส่งได้\n\n' +
          'ต้องการข้ามไปหน้าสรุปและส่งหรือไม่?'
        );
        if (!confirmSkip) {
          return;
        }
      }
      setCurrentStep(6);
    };

    if (largeItems.length === 0) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-600 mb-4">ไม่มีอุปกรณ์ชิ้นใหญ่</p>
          <button
            onClick={handleSkipToReview}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            ข้ามไปสรุปและส่ง
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-gray-700">
            <strong>💡 เคล็ดลับ:</strong><br />
            เลือกอุปกรณ์ที่ต้องการตรวจสอบ สามารถตรวจสอบได้ไม่เรียงลำดับ และระบุเตียงได้ (ไม่บังคับ)
          </p>
        </div>

        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">
            🩺 เลือกอุปกรณ์ชิ้นใหญ่ที่จะตรวจสอบ
          </h3>
          <div className="flex gap-2">
            <button
              onClick={handleSelectAll}
              className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
            >
              เลือกทั้งหมด
            </button>
            <button
              onClick={handleDeselectAll}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            >
              ยกเลิกทั้งหมด
            </button>
          </div>
        </div>

        <div className="space-y-2">
          {largeItems.map(item => {
            const isSelected = selectedLargeItems.includes(item.id);
            return (
              <label
                key={item.id}
                className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleToggleItem(item.id)}
                  className="mr-3 w-5 h-5 text-blue-600 flex-shrink-0"
                />
                {/* Equipment Image */}
                {(() => {
                  const equipmentImage = (item.images && Array.isArray(item.images) && item.images.length > 0)
                    ? (typeof item.images[0] === 'string' ? item.images[0] : item.images[0]?.url)
                    : (item.thumbnail || null);
                  
                  return equipmentImage ? (
                    <div className="w-12 h-12 flex-shrink-0 mr-3 rounded-lg overflow-hidden bg-gray-100">
                      <img
                        src={equipmentImage}
                        alt={item.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.classList.add('bg-gray-200');
                          e.target.parentElement.innerHTML = '<span class="text-gray-400 text-lg flex items-center justify-center h-full">🩺</span>';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-12 h-12 flex-shrink-0 mr-3 rounded-lg bg-gray-200 flex items-center justify-center">
                      <span className="text-gray-400 text-lg">🩺</span>
                    </div>
                  );
                })()}
                <div className="flex-1">
                  <div className="font-medium text-gray-800">{item.name}</div>
                  <div className="text-sm text-gray-600">
                    {item.code} • สถานะ: {item.status === EQUIPMENT_STATUS.AVAILABLE ? 'พร้อมใช้' :
                                         item.status === EQUIPMENT_STATUS.IN_USE ? 'กำลังใช้งาน' :
                                         item.status === EQUIPMENT_STATUS.MAINTENANCE ? 'บำรุงรักษา' :
                                         item.status === EQUIPMENT_STATUS.REPAIR ? 'อยู่ระหว่างส่งซ่อม' :
                                         item.status}
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm font-medium text-gray-700">
            📊 เลือกแล้ว: {selectedLargeItems.length}/{largeItems.length} รายการ
          </p>
        </div>

        {/* Skip Option */}
        {selectedLargeItems.length === 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-gray-700 mb-3">
              <strong>⚠️ ยังไม่ได้เลือกอุปกรณ์ชิ้นใหญ่</strong>
            </p>
            <p className="text-sm text-gray-600 mb-3">
              หากไม่มีอุปกรณ์ชิ้นใหญ่ที่ต้องการตรวจสอบ คุณสามารถข้ามไปหน้าสรุปและส่งได้
            </p>
            <button
              onClick={handleSkipToReview}
              className="w-full px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 font-semibold transition-colors"
            >
              ⏭️ ข้ามไปหน้าสรุปและส่ง
            </button>
          </div>
        )}
      </div>
    );
  };

  // Step 5: Inspect Large Items (V2 - Flexible Order + Optional Bed)
  const Step5InspectLargeItems = () => {
    const currentItem = currentInspectingItem?.type === 'large' 
      ? largeItems.find(eq => eq.id === currentInspectingItem.id)
      : null;
    
    const itemResult = currentItem ? (largeItemsResults[currentItem.id] || {}) : {};
    
    // Use refs to track focused input
    const notesInputRef = useRef(null);
    const [focusedInput, setFocusedInput] = useState(null);
    
    // Use local state for input values
    const [localValues, setLocalValues] = useState({
      status: EQUIPMENT_STATUS.AVAILABLE,
      condition: 'GOOD',
      hasIssue: false,
      issueType: null,
      bedId: '',
      notes: ''
    });
    
    // State for success feedback
    const [saveSuccess, setSaveSuccess] = useState(false);

    // Sync local state with global state when item changes
    useEffect(() => {
      if (currentItem) {
        const savedResult = largeItemsResults[currentItem.id];
        const status = savedResult?.status || currentItem.status || EQUIPMENT_STATUS.AVAILABLE;
        setLocalValues({
          status: status,
          condition: savedResult?.condition || 'GOOD',
          // If status is IN_USE, force hasIssue to false and issueType to null
          hasIssue: status === EQUIPMENT_STATUS.IN_USE ? false : (savedResult?.hasIssue || false),
          issueType: status === EQUIPMENT_STATUS.IN_USE ? null : (savedResult?.issueType || null),
          bedId: savedResult?.bedId || '',
          notes: savedResult?.notes || ''
        });
        setFocusedInput(null);
      }
    }, [currentItem?.id]);

    // Get available beds for dropdown
    const availableBeds = getAvailableBeds ? getAvailableBeds() : [];

    const handleSaveItem = () => {
      if (!currentItem) {
        console.error('handleSaveItem: currentItem is null');
        return;
      }

      console.log('handleSaveItem called (Large Item)', { currentItem: currentItem.id, localValues });

      const itemData = {
        equipmentId: currentItem.id,
        equipmentName: currentItem.name,
        equipmentCode: currentItem.code,
        type: EQUIPMENT_TYPES.LARGE_ITEM,
        status: localValues.status,
        condition: localValues.condition,
        hasIssue: localValues.hasIssue,
        issueType: localValues.issueType,
        bedId: localValues.bedId || null,
        notes: localValues.notes || ''
      };

      console.log('Saving large item data:', itemData);

      setLargeItemsResults(prev => {
        const updated = {
          ...prev,
          [currentItem.id]: itemData
        };
        console.log('Updated largeItemsResults:', updated);
        return updated;
      });

      console.log('Large item saved successfully');

      // Show visual feedback
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);

      // Show success feedback and navigate
      const currentIndex = selectedLargeItems.findIndex(id => id === currentItem.id);
      const remainingItems = selectedLargeItems.length - (currentIndex + 1);
      
      if (remainingItems > 0) {
        // Show success message and move to next item
        setTimeout(() => {
          alert(`✅ บันทึกสำเร็จ!\n\nบันทึกข้อมูล "${currentItem.name}" แล้ว\n\nเหลืออีก ${remainingItems} รายการ`);
          setCurrentInspectingItem({ type: 'large', id: selectedLargeItems[currentIndex + 1] });
        }, 100);
      } else {
        // All items completed - navigate to Step 6 (Review and Submit)
        setTimeout(() => {
          alert(`✅ บันทึกสำเร็จ!\n\nบันทึกข้อมูล "${currentItem.name}" แล้ว\n\nตรวจสอบครบทุกรายการแล้ว\n\nจะไปหน้าสรุปและส่ง`);
          setCurrentStep(6);
          console.log('All large items completed, navigating to Step 6');
        }, 100);
      }
    };

    if (selectedLargeItems.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
            <p className="text-lg font-semibold text-gray-800 mb-2">
              ⚠️ ยังไม่ได้เลือกอุปกรณ์ชิ้นใหญ่
            </p>
            <p className="text-sm text-gray-600 mb-4">
              กรุณาเลือกอุปกรณ์ชิ้นใหญ่ที่ต้องการตรวจสอบ หรือข้ามไปหน้าสรุปและส่ง
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setCurrentStep(4)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
              >
                ← กลับไปเลือกอุปกรณ์ชิ้นใหญ่
              </button>
              <button
                onClick={() => setCurrentStep(6)}
                className="px-6 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 font-semibold"
              >
                ⏭️ ข้ามไปหน้าสรุปและส่ง
              </button>
            </div>
          </div>
        </div>
      );
    }

    const completedCount = Object.keys(largeItemsResults).length;
    const selectedItems = selectedLargeItems.map(id => largeItems.find(eq => eq.id === id)).filter(Boolean);

    return (
      <div className="space-y-6">
        {/* Selected Items List (Top) */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            📋 รายการที่เลือก ({selectedLargeItems.length} รายการ)
          </h3>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {selectedItems.map(item => {
              const isDone = largeItemsResults[item.id];
              const isCurrent = currentInspectingItem?.id === item.id;
              return (
                <div
                  key={item.id}
                  onClick={() => setCurrentInspectingItem({ type: 'large', id: item.id })}
                  className={`flex items-center p-2 rounded cursor-pointer transition ${
                    isCurrent ? 'bg-blue-100 border-2 border-blue-500' : 
                    isDone ? 'bg-green-50 border border-green-200' : 
                    'bg-gray-50 border border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <span className="mr-2">
                    {isDone ? '✅' : isCurrent ? '🔄' : '⏳'}
                  </span>
                  {/* Equipment Image */}
                  {(() => {
                    const equipmentImage = (item.images && Array.isArray(item.images) && item.images.length > 0)
                      ? (typeof item.images[0] === 'string' ? item.images[0] : item.images[0]?.url)
                      : (item.thumbnail || null);
                    
                    return equipmentImage ? (
                      <div className="w-8 h-8 flex-shrink-0 mr-2 rounded overflow-hidden bg-gray-100">
                        <img
                          src={equipmentImage}
                          alt={item.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.classList.add('bg-gray-200');
                            e.target.parentElement.innerHTML = '<span class="text-gray-400 text-xs flex items-center justify-center h-full">🩺</span>';
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-8 h-8 flex-shrink-0 mr-2 rounded bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-400 text-xs">🩺</span>
                      </div>
                    );
                  })()}
                  <span className="text-sm text-gray-800 flex-1">{item.name}</span>
                  {isCurrent && <span className="ml-auto text-xs text-blue-600">(กำลังตรวจ)</span>}
                  {isDone && !isCurrent && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentInspectingItem({ type: 'large', id: item.id });
                      }}
                      className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      ดูผล
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Current Item Form (Bottom) */}
        {currentItem && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              🩺 {currentItem.name} ({currentItem.code})
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📊 สถานะอุปกรณ์:
                </label>
                <select
                  value={localValues.status}
                  onChange={(e) => {
                    const newStatus = e.target.value;
                    setLocalValues(prev => ({
                      ...prev,
                      status: newStatus,
                      // If changing to IN_USE, clear issue
                      hasIssue: newStatus === EQUIPMENT_STATUS.IN_USE ? false : prev.hasIssue,
                      issueType: newStatus === EQUIPMENT_STATUS.IN_USE ? null : prev.issueType
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value={EQUIPMENT_STATUS.AVAILABLE}>พร้อมใช้</option>
                  <option value={EQUIPMENT_STATUS.IN_USE}>กำลังใช้งาน</option>
                  <option value={EQUIPMENT_STATUS.MAINTENANCE}>บำรุงรักษา</option>
                  <option value={EQUIPMENT_STATUS.REPAIR}>อยู่ระหว่างส่งซ่อม</option>
                  <option value={EQUIPMENT_STATUS.UNAVAILABLE}>ไม่พร้อมใช้งาน</option>
                </select>
              </div>

              {/* Bed Selection (Optional) */}
              {localValues.status === EQUIPMENT_STATUS.IN_USE && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    🛏️ เตียงผู้ป่วย (ไม่บังคับ):
                  </label>
                  <select
                    value={localValues.bedId}
                    onChange={(e) => setLocalValues(prev => ({ ...prev, bedId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">ไม่ระบุเตียง</option>
                    {availableBeds.map(bed => (
                      <option key={bed.id} value={bed.id}>
                        {bed.bedName} ({bed.bedNumber}) - {bed.ward}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  สภาพ:
                </label>
                <select
                  value={localValues.condition}
                  onChange={(e) => setLocalValues(prev => ({ ...prev, condition: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="GOOD">ดี</option>
                  <option value="DAMAGED">เสียหาย</option>
                  <option value="NEEDS_REPAIR">ต้องซ่อม</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ⚠️ ปัญหา:
                </label>
                {localValues.status === EQUIPMENT_STATUS.IN_USE ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      <strong>🔒 ล็อค:</strong> อุปกรณ์กำลังใช้งานอยู่ ไม่สามารถระบุปัญหาได้
                      <br />
                      <span className="text-xs text-yellow-600 mt-1 block">
                        หากอุปกรณ์มีปัญหา กรุณาเปลี่ยนสถานะเป็น "พร้อมใช้" หรือ "บำรุงรักษา" ก่อน
                      </span>
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name={`issue-${currentItem.id}`}
                        checked={!localValues.hasIssue}
                        onChange={() => setLocalValues(prev => ({ ...prev, hasIssue: false, issueType: null }))}
                        className="mr-2"
                      />
                      <span>ไม่มี</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name={`issue-${currentItem.id}`}
                        checked={localValues.hasIssue && localValues.issueType === 'DAMAGED'}
                        onChange={() => setLocalValues(prev => ({ ...prev, hasIssue: true, issueType: 'DAMAGED' }))}
                        className="mr-2"
                      />
                      <span>เสียหาย</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name={`issue-${currentItem.id}`}
                        checked={localValues.hasIssue && localValues.issueType === 'NEEDS_REPAIR'}
                        onChange={() => setLocalValues(prev => ({ ...prev, hasIssue: true, issueType: 'NEEDS_REPAIR' }))}
                        className="mr-2"
                      />
                      <span>ต้องซ่อม</span>
                    </label>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  หมายเหตุ
                </label>
                <textarea
                  ref={notesInputRef}
                  value={localValues.notes}
                  onFocus={() => setFocusedInput('notes')}
                  onBlur={() => setFocusedInput(null)}
                  onChange={(e) => setLocalValues(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="เพิ่มหมายเหตุ..."
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3 relative" style={{ zIndex: 60 }}>
              {/* Success Message */}
              {saveSuccess && (
                <div className="absolute -top-12 left-0 right-0 flex justify-center animate-bounce">
                  <div className="bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
                    <span className="text-xl">✅</span>
                    <span className="font-semibold">บันทึกสำเร็จ!</span>
                  </div>
                </div>
              )}
              
              <button
                type="button"
                onClick={(e) => {
                  console.log('Save button clicked (Large Item) - START', { 
                    currentItem: currentItem?.id, 
                    disabled: e.currentTarget.disabled,
                    localValues
                  });
                  
                  e.preventDefault();
                  e.stopPropagation();
                  
                  console.log('Save button clicked (Large Item) - calling handleSaveItem');
                  handleSaveItem();
                  console.log('Save button clicked (Large Item) - handleSaveItem called');
                }}
                className={`flex-1 px-4 py-2 text-white rounded-lg transition-all ${
                  saveSuccess
                    ? 'bg-green-500 animate-pulse'
                    : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800 cursor-pointer'
                }`}
                style={{ 
                  position: 'relative',
                  zIndex: 100,
                  pointerEvents: 'auto',
                  touchAction: 'manipulation'
                }}
              >
                {saveSuccess ? '✅ บันทึกแล้ว' : '💾 บันทึก'}
              </button>
              {completedCount < selectedLargeItems.length && (
                <button
                  type="button"
                  onClick={() => {
                    console.log('Save and Next button clicked (Large Item)');
                    handleSaveItem();
                    const currentIndex = selectedLargeItems.findIndex(id => id === currentItem.id);
                    if (currentIndex < selectedLargeItems.length - 1) {
                      setCurrentInspectingItem({ type: 'large', id: selectedLargeItems[currentIndex + 1] });
                    }
                  }}
                  className={`flex-1 px-4 py-2 bg-green-600 text-white rounded-lg transition-all ${
                    saveSuccess
                      ? 'bg-green-500 animate-pulse'
                      : 'hover:bg-green-700 active:bg-green-800 cursor-pointer'
                  }`}
                  style={{ 
                    position: 'relative',
                    zIndex: 100,
                    pointerEvents: 'auto',
                    touchAction: 'manipulation'
                  }}
                >
                  บันทึกและถัดไป →
                </button>
              )}
            </div>
          </div>
        )}

        <div className="text-center text-sm text-gray-600">
          📊 ความคืบหน้า: {completedCount}/{selectedLargeItems.length} รายการ
        </div>
      </div>
    );
  };

  // Step 6: Review and Submit (V2 - Updated)
  const Step6ReviewAndSubmit = () => {
    const smallItemsData = Object.values(smallItemsResults);
    const largeItemsData = Object.values(largeItemsResults);

    const totalAvailable = smallItemsData.reduce((sum, item) => sum + (parseInt(item.availableQuantity) || 0), 0);
    const totalDamaged = smallItemsData.reduce((sum, item) => sum + (parseInt(item.damagedQuantity) || 0), 0);
    const totalLost = smallItemsData.reduce((sum, item) => sum + (parseInt(item.lostQuantity) || 0), 0);

    const availableLarge = largeItemsData.filter(item => item.status === EQUIPMENT_STATUS.AVAILABLE).length;
    const inUseLarge = largeItemsData.filter(item => item.status === EQUIPMENT_STATUS.IN_USE).length;
    const borrowedLarge = largeItemsData.filter(item => item.status === EQUIPMENT_STATUS.BORROWED).length;
    const repairLarge = largeItemsData.filter(item => item.status === EQUIPMENT_STATUS.REPAIR || item.issueType === 'NEEDS_REPAIR').length;

    // Helper function to update usageCount for reasons
    const updateReasonsUsageCount = async (allItems) => {
      const usedDamageReasons = new Set();
      const usedLossReasons = new Set();
      
      allItems.forEach(item => {
        if (item.damagedQuantity > 0 && item.damagedReason && item.damagedReason !== 'OTHER') {
          usedDamageReasons.add(item.damagedReason);
        }
        if (item.lostQuantity > 0 && item.lostReason && item.lostReason !== 'OTHER') {
          usedLossReasons.add(item.lostReason);
        }
      });
      
      // Increment usageCount for damage reasons
      for (const reason of usedDamageReasons) {
        const reasonDoc = damageReasons.find(r => r.reason === reason);
        if (reasonDoc && reasonDoc.id && !reasonDoc.id.startsWith('default')) {
          try {
            const reasonRef = doc(db, 'equipmentDamageReasons', reasonDoc.id);
            await updateDoc(reasonRef, {
              usageCount: increment(1),
              updatedAt: serverTimestamp()
            });
          } catch (err) {
            console.error('Error updating damage reason usage:', err);
          }
        }
      }
      
      // Increment usageCount for loss reasons
      for (const reason of usedLossReasons) {
        const reasonDoc = lossReasons.find(r => r.reason === reason);
        if (reasonDoc && reasonDoc.id && !reasonDoc.id.startsWith('default')) {
          try {
            const reasonRef = doc(db, 'equipmentLossReasons', reasonDoc.id);
            await updateDoc(reasonRef, {
              usageCount: increment(1),
              updatedAt: serverTimestamp()
            });
          } catch (err) {
            console.error('Error updating loss reason usage:', err);
          }
        }
      }
    };

    // Save Draft
    const handleSaveDraft = async () => {
      try {
        setIsSaving(true);
        const allItems = [
          ...smallItemsData,
          ...largeItemsData
        ];

        let result;
        if (currentInspectionId) {
          result = await updateInspection(currentInspectionId, {
            date: inspectionData.date,
            round: inspectionData.round,
            shiftType: inspectionData.shiftType,
            items: allItems,
            notes: inspectionData.notes,
            status: 'DRAFT'
          });
        } else {
          result = await createInspection({
            date: inspectionData.date,
            round: inspectionData.round,
            shiftType: inspectionData.shiftType,
            items: allItems,
            notes: inspectionData.notes,
            status: 'DRAFT'
          });
          
          if (result.success && result.inspectionId) {
            setCurrentInspectionId(result.inspectionId);
            setInspectionStatus('DRAFT');
          }
        }

        if (result.success) {
          alert('บันทึกชั่วคราวสำเร็จ');
        } else {
          alert(`เกิดข้อผิดพลาด: ${result.error}`);
        }
      } catch (err) {
        console.error('Error saving draft:', err);
        alert(`เกิดข้อผิดพลาด: ${err.message}`);
      } finally {
        setIsSaving(false);
      }
    };

    // Submit for Approval
    const handleSubmitForApproval = async () => {
      try {
        // Validation: Must have at least 1 inspected item
        const totalInspectedItems = smallItemsData.length + largeItemsData.length;
        if (totalInspectedItems === 0) {
          alert('⚠️ ไม่สามารถส่งผลการตรวจสอบได้\n\nกรุณาตรวจสอบอุปกรณ์อย่างน้อย 1 รายการ (ชิ้นเล็กหรือชิ้นใหญ่)');
          return;
        }

        setIsSaving(true);
        const allItems = [
          ...smallItemsData,
          ...largeItemsData
        ];

        let result;
        if (currentInspectionId) {
          const updateResult = await updateInspection(currentInspectionId, {
            date: inspectionData.date,
            round: inspectionData.round,
            shiftType: inspectionData.shiftType,
            items: allItems,
            notes: inspectionData.notes
          });
          
          if (updateResult.success) {
            result = await submitInspection(currentInspectionId);
            if (result.success) {
              setInspectionStatus('PENDING_APPROVAL');
            }
          } else {
            result = updateResult;
          }
        } else {
          const createResult = await createInspection({
            date: inspectionData.date,
            round: inspectionData.round,
            shiftType: inspectionData.shiftType,
            items: allItems,
            notes: inspectionData.notes,
            status: 'DRAFT'
          });
          
          if (createResult.success && createResult.inspectionId) {
            setCurrentInspectionId(createResult.inspectionId);
            result = await submitInspection(createResult.inspectionId);
            if (result.success) {
              setInspectionStatus('PENDING_APPROVAL');
            }
          } else {
            result = createResult;
          }
        }

        if (result.success) {
          await updateReasonsUsageCount(allItems);
          
          navigate('/nurse/equipment/inspection/success', {
            state: {
              inspectionId: currentInspectionId,
              totalItems: allItems.length,
              status: 'PENDING_APPROVAL'
            }
          });
        } else {
          alert(`เกิดข้อผิดพลาด: ${result.error}`);
        }
      } catch (err) {
        console.error('Error submitting inspection:', err);
        alert(`เกิดข้อผิดพลาด: ${err.message}`);
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <div className="space-y-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">
            📋 สรุปการตรวจสอบ
          </h3>

          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-gray-700 mb-2">
                📦 ชิ้นเล็ก ({smallItemsData.length} รายการ)
              </h4>
              <div className="space-y-2">
                {smallItemsData.map((item, index) => {
                  const variance = (parseInt(item.availableQuantity || 0) + 
                                   parseInt(item.damagedQuantity || 0) + 
                                   parseInt(item.lostQuantity || 0)) - 
                                   (item.previousStock || 0);
                  return (
                    <div
                      key={index}
                      className={`p-3 rounded-lg ${
                        variance !== 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-800">
                            {variance !== 0 ? '⚠️' : '✅'} {item.equipmentName}
                          </p>
                          <p className="text-sm text-gray-600">
                            คงเหลือ: {item.availableQuantity || 0}
                            {item.damagedQuantity > 0 && `, เสียหาย: ${item.damagedQuantity}`}
                            {item.lostQuantity > 0 && `, สูญหาย: ${item.lostQuantity}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-700 mb-2">
                🩺 ชิ้นใหญ่ ({largeItemsData.length} รายการ)
              </h4>
              <div className="space-y-2">
                {largeItemsData.map((item, index) => {
                  const hasIssue = item.hasIssue || item.status === EQUIPMENT_STATUS.REPAIR;
                  return (
                    <div
                      key={index}
                      className={`p-3 rounded-lg ${
                        hasIssue ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'
                      }`}
                    >
                      <p className="font-medium text-gray-800">
                        {hasIssue ? '⚠️' : '✅'} {item.equipmentName}
                      </p>
                      <p className="text-sm text-gray-600">
                        สถานะ: {item.status === EQUIPMENT_STATUS.AVAILABLE ? 'พร้อมใช้' :
                               item.status === EQUIPMENT_STATUS.IN_USE ? 'กำลังใช้งาน' :
                               item.status === EQUIPMENT_STATUS.BORROWED ? 'ถูกยืม' :
                               item.status === EQUIPMENT_STATUS.REPAIR ? 'อยู่ระหว่างส่งซ่อม' : item.status}
                        {item.bedId && ` • เตียง: ${item.bedId}`}
                        {hasIssue && item.issueType && ` (${item.issueType === 'DAMAGED' ? 'เสียหาย' : 'ต้องซ่อม'})`}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Issues Summary */}
        {(totalDamaged > 0 || totalLost > 0 || repairLarge > 0) && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-2">⚠️ รายการที่ต้องระวัง:</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
              {totalDamaged > 0 && <li>ชิ้นเล็ก: เสียหาย {totalDamaged} รายการ</li>}
              {totalLost > 0 && <li>ชิ้นเล็ก: สูญหาย {totalLost} รายการ</li>}
              {repairLarge > 0 && <li>ชิ้นใหญ่: อยู่ระหว่างส่งซ่อม {repairLarge} เครื่อง</li>}
            </ul>
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">📊 สรุปยอด:</h3>
          
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-gray-700 mb-2">ชิ้นเล็ก:</h4>
              <ul className="text-sm text-gray-600 space-y-1 ml-4">
                <li>• คงเหลือ: {totalAvailable} รายการ</li>
                <li>• เสียหาย: {totalDamaged} รายการ</li>
                <li>• สูญหาย: {totalLost} รายการ</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-700 mb-2">ชิ้นใหญ่:</h4>
              <ul className="text-sm text-gray-600 space-y-1 ml-4">
                <li>• พร้อมใช้: {availableLarge} เครื่อง</li>
                <li>• กำลังใช้งาน: {inUseLarge} เครื่อง</li>
                <li>• ถูกยืม: {borrowedLarge} เครื่อง</li>
                <li>• อยู่ระหว่างส่งซ่อม: {repairLarge} เครื่อง</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-600">
            <strong>👤 ผู้ตรวจสอบ:</strong> {currentUser?.displayName || currentUser?.email}
          </p>
          <p className="text-sm text-gray-600 mt-1">
            <strong>📅 วันที่/เวลา:</strong> {inspectionData.date.toLocaleDateString('th-TH')}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            📝 หมายเหตุ
          </label>
          <textarea
            value={inspectionData.notes}
            onChange={(e) => setInspectionData({
              ...inspectionData,
              notes: e.target.value
            })}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="เพิ่มหมายเหตุ..."
          />
        </div>

        {/* Status Badge */}
        {inspectionStatus !== 'DRAFT' && (
          <div className="mb-4">
            <div className={`inline-flex items-center px-4 py-2 rounded-lg ${
              inspectionStatus === 'PENDING_APPROVAL' ? 'bg-yellow-100 text-yellow-800' :
              inspectionStatus === 'APPROVED' ? 'bg-green-100 text-green-800' :
              inspectionStatus === 'REJECTED' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              <span className="mr-2">
                {inspectionStatus === 'PENDING_APPROVAL' ? '⏳' :
                 inspectionStatus === 'APPROVED' ? '✅' :
                 inspectionStatus === 'REJECTED' ? '❌' : '📝'}
              </span>
              <span className="font-semibold">
                {inspectionStatus === 'PENDING_APPROVAL' ? 'รออนุมัติ' :
                 inspectionStatus === 'APPROVED' ? 'อนุมัติแล้ว' :
                 inspectionStatus === 'REJECTED' ? 'ไม่อนุมัติ' : 'Draft'}
              </span>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleSaveDraft}
            disabled={isSaving}
            className="w-full px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'กำลังบันทึก...' : '💾 บันทึกชั่วคราว'}
          </button>
          
          <button
            onClick={handleSubmitForApproval}
            disabled={isSaving || inspectionStatus === 'APPROVED'}
            className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'กำลังส่ง...' : inspectionStatus === 'PENDING_APPROVAL' ? '📤 ส่งใหม่' : '📤 ส่งผลการตรวจสอบ'}
          </button>
        </div>
      </div>
    );
  };

  // Step 3: Inspect Large Items (OLD - Keep for reference, will be replaced)
  const Step3InspectLargeItems_OLD = () => {
    const [currentItemIndex, setCurrentItemIndex] = useState(0);
    const currentItem = largeItems[currentItemIndex];
    const itemProgress = largeItemsResults[currentItem?.id] || {};

    const handleSaveItem = () => {
      if (!currentItem) return;

      const itemData = {
        equipmentId: currentItem.id,
        equipmentName: currentItem.name,
        equipmentCode: currentItem.code,
        type: EQUIPMENT_TYPES.LARGE_ITEM,
        status: itemProgress.status || EQUIPMENT_STATUS.AVAILABLE,
        condition: itemProgress.condition || 'GOOD',
        hasIssue: itemProgress.hasIssue || false,
        issueType: itemProgress.issueType || null,
        notes: itemProgress.notes || ''
      };

      setLargeItemsResults({
        ...largeItemsResults,
        [currentItem.id]: itemData
      });

      if (currentItemIndex < largeItems.length - 1) {
        setCurrentItemIndex(currentItemIndex + 1);
      }
    };

    if (largeItems.length === 0) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-600">ไม่มีอุปกรณ์ชิ้นใหญ่</p>
          <button
            onClick={() => setCurrentStep(4)}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            ข้ามไปขั้นตอนถัดไป
          </button>
        </div>
      );
    }

    const completedCount = Object.keys(largeItemsResults).length;

    return (
      <div className="space-y-6">
        {/* Progress List */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            📋 รายการอุปกรณ์ ({largeItems.length} รายการ)
          </h3>
          <div className="space-y-2">
            {largeItems.map((item, index) => {
              const isDone = largeItemsResults[item.id];
              const isCurrent = index === currentItemIndex;
              return (
                <div
                  key={item.id}
                  className={`flex items-center p-2 rounded ${
                    isCurrent ? 'bg-blue-100' : isDone ? 'bg-green-50' : 'bg-gray-50'
                  }`}
                >
                  <span className="mr-2">
                    {isDone ? '✅' : isCurrent ? '🔄' : '⏳'}
                  </span>
                  <span className="text-sm text-gray-800">{item.name}</span>
                  {isCurrent && <span className="ml-auto text-xs text-blue-600">(กำลังตรวจ)</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Current Item Form */}
        {currentItem && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              🩺 {currentItem.name} ({currentItem.code})
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📊 สถานะอุปกรณ์:
                </label>
                <select
                  value={itemProgress.status || EQUIPMENT_STATUS.AVAILABLE}
                  onChange={(e) => setLargeItemsResults({
                    ...largeItemsResults,
                    [currentItem.id]: {
                      ...itemProgress,
                      status: e.target.value
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value={EQUIPMENT_STATUS.AVAILABLE}>พร้อมใช้</option>
                  <option value={EQUIPMENT_STATUS.BORROWED}>ถูกยืม</option>
                  <option value={EQUIPMENT_STATUS.MAINTENANCE}>บำรุงรักษา</option>
                  <option value={EQUIPMENT_STATUS.REPAIR}>อยู่ระหว่างส่งซ่อม</option>
                  <option value={EQUIPMENT_STATUS.UNAVAILABLE}>ไม่พร้อมใช้งาน</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  สภาพ:
                </label>
                <select
                  value={itemProgress.condition || 'GOOD'}
                  onChange={(e) => setLargeItemsResults({
                    ...largeItemsResults,
                    [currentItem.id]: {
                      ...itemProgress,
                      condition: e.target.value
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="GOOD">ดี</option>
                  <option value="DAMAGED">เสียหาย</option>
                  <option value="NEEDS_REPAIR">ต้องซ่อม</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ⚠️ ปัญหา:
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name={`issue-${currentItem.id}`}
                      checked={!itemProgress.hasIssue}
                      onChange={() => setLargeItemsResults({
                        ...largeItemsResults,
                        [currentItem.id]: {
                          ...itemProgress,
                          hasIssue: false,
                          issueType: null
                        }
                      })}
                      className="mr-2"
                    />
                    <span>ไม่มี</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name={`issue-${currentItem.id}`}
                      checked={itemProgress.hasIssue && itemProgress.issueType === 'DAMAGED'}
                      onChange={() => setLargeItemsResults({
                        ...largeItemsResults,
                        [currentItem.id]: {
                          ...itemProgress,
                          hasIssue: true,
                          issueType: 'DAMAGED'
                        }
                      })}
                      className="mr-2"
                    />
                    <span>เสียหาย</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name={`issue-${currentItem.id}`}
                      checked={itemProgress.hasIssue && itemProgress.issueType === 'NEEDS_REPAIR'}
                      onChange={() => setLargeItemsResults({
                        ...largeItemsResults,
                        [currentItem.id]: {
                          ...itemProgress,
                          hasIssue: true,
                          issueType: 'NEEDS_REPAIR'
                        }
                      })}
                      className="mr-2"
                    />
                    <span>ต้องซ่อม</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  หมายเหตุ
                </label>
                <textarea
                  value={itemProgress.notes || ''}
                  onChange={(e) => setLargeItemsResults({
                    ...largeItemsResults,
                    [currentItem.id]: {
                      ...itemProgress,
                      notes: e.target.value
                    }
                  })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="เพิ่มหมายเหตุ..."
                />
              </div>
            </div>

            <div className="mt-6">
              <button
                type="button"
                onClick={handleSaveItem}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                บันทึกและถัดไป →
              </button>
            </div>
          </div>
        )}

        <div className="text-center text-sm text-gray-600">
          📊 ความคืบหน้า: {completedCount}/{largeItems.length} รายการ
        </div>
      </div>
    );
  };

  // Step 4: Review Summary
  const Step4ReviewSummary = () => {
    const smallItemsData = Object.values(smallItemsResults);
    const largeItemsData = Object.values(largeItemsResults);

    const totalAvailable = smallItemsData.reduce((sum, item) => sum + (parseInt(item.availableQuantity) || 0), 0);
    const totalDamaged = smallItemsData.reduce((sum, item) => sum + (parseInt(item.damagedQuantity) || 0), 0);
    const totalLost = smallItemsData.reduce((sum, item) => sum + (parseInt(item.lostQuantity) || 0), 0);

    const availableLarge = largeItemsData.filter(item => item.status === EQUIPMENT_STATUS.AVAILABLE).length;
    const borrowedLarge = largeItemsData.filter(item => item.status === EQUIPMENT_STATUS.BORROWED).length;
    const repairLarge = largeItemsData.filter(item => item.status === EQUIPMENT_STATUS.REPAIR || item.issueType === 'NEEDS_REPAIR').length;

    return (
      <div className="space-y-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">
            📋 สรุปการตรวจสอบ
          </h3>

          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-gray-700 mb-2">
                📦 ชิ้นเล็ก ({smallItemsData.length} รายการ)
              </h4>
              <div className="space-y-2">
                {smallItemsData.map((item, index) => {
                  const variance = (parseInt(item.availableQuantity || 0) + 
                                   parseInt(item.damagedQuantity || 0) + 
                                   parseInt(item.lostQuantity || 0)) - 
                                   (item.previousStock || 0);
                  return (
                    <div
                      key={index}
                      className={`p-3 rounded-lg ${
                        variance !== 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-800">
                            {variance !== 0 ? '⚠️' : '✅'} {item.equipmentName}
                          </p>
                          <p className="text-sm text-gray-600">
                            คงเหลือ: {item.availableQuantity || 0}
                            {item.damagedQuantity > 0 && `, เสียหาย: ${item.damagedQuantity}`}
                            {item.lostQuantity > 0 && `, สูญหาย: ${item.lostQuantity}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-700 mb-2">
                🩺 ชิ้นใหญ่ ({largeItemsData.length} รายการ)
              </h4>
              <div className="space-y-2">
                {largeItemsData.map((item, index) => {
                  const hasIssue = item.hasIssue || item.status === EQUIPMENT_STATUS.REPAIR;
                  return (
                    <div
                      key={index}
                      className={`p-3 rounded-lg ${
                        hasIssue ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'
                      }`}
                    >
                      <p className="font-medium text-gray-800">
                        {hasIssue ? '⚠️' : '✅'} {item.equipmentName}
                      </p>
                      <p className="text-sm text-gray-600">
                        สถานะ: {item.status === EQUIPMENT_STATUS.AVAILABLE ? 'พร้อมใช้' :
                               item.status === EQUIPMENT_STATUS.BORROWED ? 'ถูกยืม' :
                               item.status === EQUIPMENT_STATUS.REPAIR ? 'อยู่ระหว่างส่งซ่อม' : item.status}
                        {hasIssue && item.issueType && ` (${item.issueType === 'DAMAGED' ? 'เสียหาย' : 'ต้องซ่อม'})`}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Issues Summary */}
        {(totalDamaged > 0 || totalLost > 0 || repairLarge > 0) && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-2">⚠️ รายการที่ต้องระวัง:</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
              {totalDamaged > 0 && <li>ชิ้นเล็ก: เสียหาย {totalDamaged} รายการ</li>}
              {totalLost > 0 && <li>ชิ้นเล็ก: สูญหาย {totalLost} รายการ</li>}
              {repairLarge > 0 && <li>ชิ้นใหญ่: อยู่ระหว่างส่งซ่อม {repairLarge} เครื่อง</li>}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Step 5: Finalize
  const Step5Finalize = () => {
    const smallItemsData = Object.values(smallItemsResults);
    const largeItemsData = Object.values(largeItemsResults);

    const totalAvailable = smallItemsData.reduce((sum, item) => sum + (parseInt(item.availableQuantity) || 0), 0);
    const totalDamaged = smallItemsData.reduce((sum, item) => sum + (parseInt(item.damagedQuantity) || 0), 0);
    const totalLost = smallItemsData.reduce((sum, item) => sum + (parseInt(item.lostQuantity) || 0), 0);

    const availableLarge = largeItemsData.filter(item => item.status === EQUIPMENT_STATUS.AVAILABLE).length;
    const borrowedLarge = largeItemsData.filter(item => item.status === EQUIPMENT_STATUS.BORROWED).length;
    const repairLarge = largeItemsData.filter(item => item.status === EQUIPMENT_STATUS.REPAIR || item.issueType === 'NEEDS_REPAIR').length;

    // Helper function to update usageCount for reasons
    const updateReasonsUsageCount = async (allItems) => {
      const usedDamageReasons = new Set();
      const usedLossReasons = new Set();
      
      allItems.forEach(item => {
        if (item.damagedQuantity > 0 && item.damagedReason && item.damagedReason !== 'OTHER') {
          usedDamageReasons.add(item.damagedReason);
        }
        if (item.lostQuantity > 0 && item.lostReason && item.lostReason !== 'OTHER') {
          usedLossReasons.add(item.lostReason);
        }
      });
      
      // Increment usageCount for damage reasons
      for (const reason of usedDamageReasons) {
        const reasonDoc = damageReasons.find(r => r.reason === reason);
        if (reasonDoc && reasonDoc.id && !reasonDoc.id.startsWith('default')) {
          try {
            const reasonRef = doc(db, 'equipmentDamageReasons', reasonDoc.id);
            await updateDoc(reasonRef, {
              usageCount: increment(1),
              updatedAt: serverTimestamp()
            });
          } catch (err) {
            console.error('Error updating damage reason usage:', err);
          }
        }
      }
      
      // Increment usageCount for loss reasons
      for (const reason of usedLossReasons) {
        const reasonDoc = lossReasons.find(r => r.reason === reason);
        if (reasonDoc && reasonDoc.id && !reasonDoc.id.startsWith('default')) {
          try {
            const reasonRef = doc(db, 'equipmentLossReasons', reasonDoc.id);
            await updateDoc(reasonRef, {
              usageCount: increment(1),
              updatedAt: serverTimestamp()
            });
          } catch (err) {
            console.error('Error updating loss reason usage:', err);
          }
        }
      }
    };

    // Save Draft
    const handleSaveDraft = async () => {
      try {
        setIsSaving(true);
        const allItems = [
          ...smallItemsData,
          ...largeItemsData
        ];

        let result;
        if (currentInspectionId) {
          // Update existing inspection
          result = await updateInspection(currentInspectionId, {
            date: inspectionData.date,
            round: inspectionData.round,
            shiftType: inspectionData.shiftType,
            items: allItems,
            notes: inspectionData.notes,
            status: 'DRAFT'
          });
        } else {
          // Create new inspection as DRAFT
          result = await createInspection({
            date: inspectionData.date,
            round: inspectionData.round,
            shiftType: inspectionData.shiftType,
            items: allItems,
            notes: inspectionData.notes,
            status: 'DRAFT'
          });
          
          if (result.success && result.inspectionId) {
            setCurrentInspectionId(result.inspectionId);
            setInspectionStatus('DRAFT');
          }
        }

        if (result.success) {
          alert('บันทึกชั่วคราวสำเร็จ');
        } else {
          alert(`เกิดข้อผิดพลาด: ${result.error}`);
        }
      } catch (err) {
        console.error('Error saving draft:', err);
        alert(`เกิดข้อผิดพลาด: ${err.message}`);
      } finally {
        setIsSaving(false);
      }
    };

    // Submit for Approval
    const handleSubmitForApproval = async () => {
      try {
        setIsSaving(true);
        const allItems = [
          ...smallItemsData,
          ...largeItemsData
        ];

        let result;
        if (currentInspectionId) {
          // Update and submit existing inspection
          const updateResult = await updateInspection(currentInspectionId, {
            date: inspectionData.date,
            round: inspectionData.round,
            shiftType: inspectionData.shiftType,
            items: allItems,
            notes: inspectionData.notes
          });
          
          if (updateResult.success) {
            result = await submitInspection(currentInspectionId);
            if (result.success) {
              setInspectionStatus('PENDING_APPROVAL');
            }
          } else {
            result = updateResult;
          }
        } else {
          // Create new inspection and submit
          const createResult = await createInspection({
            date: inspectionData.date,
            round: inspectionData.round,
            shiftType: inspectionData.shiftType,
            items: allItems,
            notes: inspectionData.notes,
            status: 'DRAFT'
          });
          
          if (createResult.success && createResult.inspectionId) {
            setCurrentInspectionId(createResult.inspectionId);
            result = await submitInspection(createResult.inspectionId);
            if (result.success) {
              setInspectionStatus('PENDING_APPROVAL');
            }
          } else {
            result = createResult;
          }
        }

        if (result.success) {
          // Update usageCount for reasons
          await updateReasonsUsageCount(allItems);
          
          navigate('/nurse/equipment/inspection/success', {
            state: {
              inspectionId: currentInspectionId,
              totalItems: allItems.length,
              status: 'PENDING_APPROVAL'
            }
          });
        } else {
          alert(`เกิดข้อผิดพลาด: ${result.error}`);
        }
      } catch (err) {
        console.error('Error submitting inspection:', err);
        alert(`เกิดข้อผิดพลาด: ${err.message}`);
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <div className="space-y-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <div className="text-4xl mb-2">✅</div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">
            การตรวจสอบเสร็จสมบูรณ์
          </h3>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">📊 สรุปยอด:</h3>
          
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-gray-700 mb-2">ชิ้นเล็ก:</h4>
              <ul className="text-sm text-gray-600 space-y-1 ml-4">
                <li>• คงเหลือ: {totalAvailable} รายการ</li>
                <li>• เสียหาย: {totalDamaged} รายการ</li>
                <li>• สูญหาย: {totalLost} รายการ</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-700 mb-2">ชิ้นใหญ่:</h4>
              <ul className="text-sm text-gray-600 space-y-1 ml-4">
                <li>• พร้อมใช้: {availableLarge} เครื่อง</li>
                <li>• ถูกยืม: {borrowedLarge} เครื่อง</li>
                <li>• อยู่ระหว่างส่งซ่อม: {repairLarge} เครื่อง</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-600">
            <strong>👤 ผู้ตรวจสอบ:</strong> {currentUser?.displayName || currentUser?.email}
          </p>
          <p className="text-sm text-gray-600 mt-1">
            <strong>📅 วันที่/เวลา:</strong> {inspectionData.date.toLocaleDateString('th-TH')}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            📝 หมายเหตุ
          </label>
          <textarea
            value={inspectionData.notes}
            onChange={(e) => setInspectionData({
              ...inspectionData,
              notes: e.target.value
            })}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="เพิ่มหมายเหตุ..."
          />
        </div>

        {/* Status Badge */}
        {inspectionStatus !== 'DRAFT' && (
          <div className="mb-4">
            <div className={`inline-flex items-center px-4 py-2 rounded-lg ${
              inspectionStatus === 'PENDING_APPROVAL' ? 'bg-yellow-100 text-yellow-800' :
              inspectionStatus === 'APPROVED' ? 'bg-green-100 text-green-800' :
              inspectionStatus === 'REJECTED' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              <span className="mr-2">
                {inspectionStatus === 'PENDING_APPROVAL' ? '⏳' :
                 inspectionStatus === 'APPROVED' ? '✅' :
                 inspectionStatus === 'REJECTED' ? '❌' : '📝'}
              </span>
              <span className="font-semibold">
                {inspectionStatus === 'PENDING_APPROVAL' ? 'รออนุมัติ' :
                 inspectionStatus === 'APPROVED' ? 'อนุมัติแล้ว' :
                 inspectionStatus === 'REJECTED' ? 'ไม่อนุมัติ' : 'Draft'}
              </span>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleSaveDraft}
            disabled={isSaving}
            className="w-full px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'กำลังบันทึก...' : '💾 บันทึกชั่วคราว'}
          </button>
          
          <button
            onClick={handleSubmitForApproval}
            disabled={isSaving || inspectionStatus === 'APPROVED'}
            className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'กำลังส่ง...' : inspectionStatus === 'PENDING_APPROVAL' ? '📤 ส่งใหม่' : '📤 ส่งผลการตรวจสอบ'}
          </button>
        </div>
      </div>
    );
  };

  // Render current step (V2 - Updated for 6 steps)
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1SelectRound />;
      case 2:
        return <Step2SelectSmallItems />;
      case 3:
        return <Step3InspectSmallItems />;
      case 4:
        return <Step4SelectLargeItems />;
      case 5:
        return <Step5InspectLargeItems />;
      case 6:
        return <Step6ReviewAndSubmit />;
      default:
        return null;
    }
  };

  const progress = calculateProgress();
  const stepLabels = [
    'เลือกรอบตรวจสอบ',
    'เลือกอุปกรณ์ชิ้นเล็ก',
    'ตรวจสอบชิ้นเล็ก',
    'เลือกอุปกรณ์ชิ้นใหญ่',
    'ตรวจสอบชิ้นใหญ่',
    'สรุปและส่ง'
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-4 pb-24">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/nurse/equipment')}
            className="mb-4 text-blue-600 hover:text-blue-700 flex items-center gap-2"
          >
            <span>←</span> กลับ
          </button>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            ตรวจสอบอุปกรณ์
          </h1>
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              📍 Step {currentStep}/6: {stepLabels[currentStep - 1]}
            </span>
            <span className="text-sm text-gray-600">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-blue-600 h-3 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          {renderStep()}
        </div>

        {/* Navigation */}
        <div className="flex gap-3">
          {currentStep > 1 && (
            <button
              onClick={() => {
                // If on Step 3 and no items selected, go back to Step 2
                if (currentStep === 3 && selectedSmallItems.length === 0) {
                  setCurrentStep(2);
                  return;
                }
                // If on Step 5 and no items selected, go back to Step 4
                if (currentStep === 5 && selectedLargeItems.length === 0) {
                  setCurrentStep(4);
                  return;
                }
                // Otherwise, go to previous step
                setCurrentStep(currentStep - 1);
              }}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              ← ย้อนกลับ
            </button>
          )}
          {currentStep < 6 && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                
                console.log('Next button clicked', { 
                  currentStep, 
                  selectedSmallItems: selectedSmallItems.length,
                  selectedLargeItems: selectedLargeItems.length,
                  smallItemsResults: Object.keys(smallItemsResults).length,
                  largeItemsResults: Object.keys(largeItemsResults).length
                });
                
                if (currentStep === 1 && !inspectionData.round) {
                  alert('กรุณาเลือกรอบตรวจสอบ');
                  return;
                }
                if (currentStep === 2) {
                  if (selectedSmallItems.length === 0) {
                    // Don't show alert, just prevent navigation
                    // User can use the skip button instead
                    return;
                  }
                }
                if (currentStep === 3 && Object.keys(smallItemsResults).length === 0) {
                  alert('กรุณาตรวจสอบอุปกรณ์ชิ้นเล็กอย่างน้อย 1 รายการ');
                  return;
                }
                if (currentStep === 4) {
                  if (selectedLargeItems.length === 0) {
                    // Don't show alert, just prevent navigation
                    // User can use the skip button instead
                    return;
                  }
                }
                if (currentStep === 5 && Object.keys(largeItemsResults).length === 0) {
                  alert('กรุณาตรวจสอบอุปกรณ์ชิ้นใหญ่อย่างน้อย 1 รายการ');
                  return;
                }
                setCurrentStep(currentStep + 1);
              }}
              disabled={
                (currentStep === 2 && selectedSmallItems.length === 0) ||
                (currentStep === 4 && selectedLargeItems.length === 0)
              }
              className={`flex-1 px-4 py-2 text-white rounded-lg transition-all ${
                (currentStep === 2 && selectedSmallItems.length === 0) ||
                (currentStep === 4 && selectedLargeItems.length === 0)
                  ? 'bg-gray-400 cursor-not-allowed opacity-50'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {currentStep === 2 && selectedSmallItems.length === 0 
                ? 'กรุณาเลือกอุปกรณ์หรือข้าม' 
                : currentStep === 4 && selectedLargeItems.length === 0
                ? 'กรุณาเลือกอุปกรณ์หรือข้าม'
                : 'ถัดไป →'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InspectionJourneyPage;

